import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import VrWatermark from "../vrWatermark/VrWatermark";
import TranslationsEditor from "../translationsEditor/TranslationsEditor";
import preventDoubleTrad from "../../helpers/prevent-double-trad";
import { CopyToClipboard } from "react-copy-to-clipboard";
import LinkIcon from "@mui/icons-material/Link";
import { useSnackbar } from "notistack";
import DownloadIcon from "@mui/icons-material/Download";

const PREFIX = "GltfInfos";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  dividerStyle: `${PREFIX}-dividerStyle`,
  translationsAlignment: `${PREFIX}-translationsAlignment`,
  usdzStyle: `${PREFIX}-usdzStyle`,
  usdzName: `${PREFIX}-usdzName`,
  buttonFontSize: `${PREFIX}-buttonFontSize`,
};

const GltfInfosContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.dividerStyle}`]: {
      height: "1px",
      backgroundColor: "grey",
      opacity: "0.8",
      marginBottom: "1rem",
    },
    [`& .${classes.translationsAlignment}`]: {
      textAlign: "left",
    },
    [`& .${classes.usdzStyle}`]: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "1rem",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    [`& .${classes.usdzName}`]: {
      marginLeft: "0.5rem !important",
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      maxWidth: "200px",
    },
    [`& .${classes.buttonFontSize}`]: {
      fontSize: "0.8rem",
    },
  };
});

const GltfInfos = ({
  fieldChangeHandler,
  description,
  externalLinks,
  title,
  buyable,
  purchasePrice,
  redirection,
  getS3UrlFromApi,
  loaders,
  usdz,
  isAIActive,
  promptAI,
  introductionAI,
  isWebXrAI,
  setInternalState,
  internalState,
  modelManagement,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteTranslation = (transIndex) => {
    let tmp = [...description.translations];
    tmp.splice(transIndex, 1);
    fieldChangeHandler({
      category: "grid",
      key: "description",
      value: { ...description, translations: tmp },
    });
  };

  const handleModifyTranslation = (value, key, transIndex) => {
    let tmp = [...description.translations];
    tmp[transIndex] = { ...tmp[transIndex], [key]: value };
    fieldChangeHandler({
      category: "grid",
      key: "description",
      value: { ...description, translations: tmp },
    });
  };

  const handleAddTranslation = (value, key) => {
    let tmp = { ...description };
    if (key === "translations") {
      const myObj = [...tmp[key]];
      const langsArray = preventDoubleTrad(myObj, value);
      tmp = { ...tmp, [key]: langsArray };
    } else {
      tmp = { ...tmp, initial: value };
    }
    fieldChangeHandler({ category: "grid", key: "description", value: tmp });
  };

  const handleExternalLinks = (value, key) => {
    fieldChangeHandler({ category: "grid", key, value });
  };

  const handleMoveTraduction = (fromIndex, toIndex) => {
    if (toIndex >= 0 && toIndex < description.translations.length) {
      let tmp = { ...description };
      const myObj = [...tmp.translations];
      const tmpElem = myObj[fromIndex];
      myObj.splice(fromIndex, 1);
      myObj.splice(toIndex, 0, tmpElem);
      tmp = { ...tmp, translations: myObj };
      fieldChangeHandler({ category: "grid", key: "description", value: tmp });
    }
  };

  return (
    <GltfInfosContainer>
      <Typography variant="body1" className={classes.marginBottom}>
        {t("myExpariences.form.modelTitle")}
      </Typography>
      <TextField
        name={`3d-title`}
        label={t("myExpariences.form.modelTitle")}
        value={title}
        id="modelTitle"
        className={classes.marginBottom}
        onChange={(e) => {
          fieldChangeHandler({
            category: "grid",
            key: "title",
            value: e.target.value,
          });
        }}
        fullWidth
        variant={"outlined"}
      />
      <Typography variant="body1" className={classes.marginBottom}>
        {t("myExpariences.form.redirectionLinkTitle")}
      </Typography>
      <TextField
        name={`3d-redirection-url`}
        label={t("myExpariences.form.redirectionLink")}
        value={redirection}
        id="redirection"
        className={classes.marginBottom}
        onChange={(e) => {
          fieldChangeHandler({
            category: "grid",
            key: "redirection",
            value: e.target.value,
          });
        }}
        fullWidth
        variant={"outlined"}
      />
      <div className={classes.usdzStyle}>
        <input
          type="file"
          accept=".usdz"
          name={`import-usdz`}
          id={`import-usdz`}
          style={{ display: "none" }}
          onClick={(e) => (e.target.value = null)} //reinitialize the value to retrigger the onChange on the same file
          onChange={(e) => {
            getS3UrlFromApi(e.target.files, true, true);
          }}
        />
        <label htmlFor={`import-usdz`}>
          <Button
            color="primary"
            size="medium"
            aria-label="add usdz model"
            component="span"
            variant="contained"
            disabled={loaders.lockAPI}
            className={classes.buttonFontSize}
          >
            {loaders.lockAPI ? (
              <CircularProgress size={28} />
            ) : (
              t("myExpariences.form.linkUsdz")
            )}
          </Button>
        </label>
        {usdz && (
          <Typography variant="body2" className={classes.usdzName}>
            {usdz.split("/")[usdz.split("/").length - 1].split("-")[1]}
          </Typography>
        )}
        {usdz && (
          <>
            <CopyToClipboard text={usdz}>
              <IconButton
                className={classes.copyLinkButton}
                aria-label="copy link"
                color="primary"
                onClick={(e) => {
                  enqueueSnackbar(t("toasts.copiedInClipboard"), {
                    variant: "info",
                  });
                  e.stopPropagation();
                }}
                size="large"
              >
                <LinkIcon />
              </IconButton>
            </CopyToClipboard>
            <a href={usdz} download>
              <IconButton variant="contained" color="primary" size="small">
                <DownloadIcon />
              </IconButton>
            </a>
          </>
        )}
      </div>
      <Divider className={classes.dividerStyle} />
      <div>
        <FormControlLabel
          control={
            <Checkbox
              color={"primary"}
              onChange={(e) =>
                setInternalState({
                  ...internalState,
                  isAIActive: e.target.checked,
                })
              }
              checked={isAIActive}
            />
          }
          label={t("myExpariences.form.activateAI")}
          className={classes.marginBottom}
          name={`is-AI-active`}
        />
        <FormControlLabel
          control={
            <Checkbox
              color={"primary"}
              onChange={(e) => {
                fieldChangeHandler({
                  category: "grid",
                  key: "isWebXrAI",
                  value: e.target.checked,
                });
              }}
              checked={isWebXrAI}
            />
          }
          label={t("myExpariences.form.activateWebAI")}
          className={classes.marginBottom}
          name={`is-webxr-AI-active`}
        />
        {(isAIActive || isWebXrAI) && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <TextField
              id="prompt AI"
              label={t("myExpariences.form.promptAI")}
              value={promptAI}
              className={classes.marginBottom}
              name={`prompt-AI`}
              multiline
              rows={5}
              onChange={(e) => {
                fieldChangeHandler({
                  category: "grid",
                  key: "promptAI",
                  value: e.target.value,
                });
              }}
              variant={"outlined"}
            />
            <TextField
              id="introduction AI"
              label={t("myExpariences.form.introductionAI")}
              value={introductionAI}
              className={classes.marginBottom}
              name={`introduction-AI`}
              multiline
              rows={5}
              onChange={(e) => {
                fieldChangeHandler({
                  category: "grid",
                  key: "introductionAI",
                  value: e.target.value,
                });
              }}
              variant={"outlined"}
            />
          </Box>
        )}
      </div>
      <Divider className={classes.dividerStyle} />
      <div>
        <FormControlLabel
          control={
            <Checkbox
              color={"primary"}
              onChange={(e) => {
                fieldChangeHandler({
                  category: "grid",
                  key: "buyable",
                  value: e.target.checked,
                });
              }}
              checked={buyable}
            />
          }
          label={t("myExpariences.form.buyable")}
          className={classes.marginBottom}
          name={`buyable-3d`}
        />
        {buyable && (
          <TextField
            id="purchase-price-amount"
            label={t("myExpariences.form.purchasePrice")}
            type="number"
            defaultValue={purchasePrice}
            className={classes.marginBottom}
            name={`purchase-price-3d`}
            onChange={(e) => {
              fieldChangeHandler({
                category: "grid",
                key: "purchasePrice",
                value: e.target.value,
              });
            }}
            variant={"outlined"}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      </div>
      <Divider className={classes.dividerStyle} />
      <Typography className={classes.marginBottom}>
        {t("configurator.modelDescription")}
      </Typography>
      <div className={classes.translationsAlignment}>
        <TranslationsEditor
          elements={description.translations}
          defaultText={description.initial}
          defaultTextKey="description"
          handleDefaultText={handleAddTranslation}
          addTranslation={handleAddTranslation}
          removeTranslation={handleDeleteTranslation}
          handleTranslation={handleModifyTranslation}
          configurator
          translate
          moveTraduction={handleMoveTraduction}
        />
      </div>
      {!modelManagement && (
        <>
          <Divider className={classes.dividerStyle} />
          <Typography className={classes.marginBottom}>
            {t("configurator.watermarks")}
          </Typography>
          <VrWatermark
            open={true}
            externalLinks={externalLinks}
            myDesignMode
            saveModifications={handleExternalLinks}
          />
        </>
      )}
    </GltfInfosContainer>
  );
};

export default GltfInfos;
